import type { Timesheet, TimesheetEntry, Employee } from '@/types/timesheet';
import type { Anomaly, AnomalyDetectionResult, EmployeeHistoricalData, AnomalySeverity } from '@/types/anomaly';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import { 
  calculateEntryVariance, 
  calculateTimesheetVarianceSummary,
  formatVariance 
} from './varianceCalculationService';

// Default configuration thresholds (used when org settings not provided)
const DEFAULT_THRESHOLDS = {
  DAILY_HOURS_LIMIT: 12,
  WEEKLY_HOURS_LIMIT: 48,
  LATE_SUBMISSION_DAYS: 3,
  HOURS_VARIANCE_THRESHOLD: 1.5, // 50% above average
};

export class AnomalyDetectionService {
  /**
   * Detect all anomalies in a timesheet
   * @param timesheet - The timesheet to check
   * @param employee - The employee who owns the timesheet
   * @param historicalData - Optional historical data for the employee
   * @param allTimesheets - Optional list of all timesheets for context
   * @param orgSettings - Optional organisation settings to derive limits from
   */
  static detectAnomalies(
    timesheet: Timesheet,
    employee: Employee,
    historicalData?: EmployeeHistoricalData,
    allTimesheets?: Timesheet[],
    orgSettings?: OrganisationTimesheetSettings
  ): AnomalyDetectionResult {
    // Derive limits from organisation settings if provided, otherwise use defaults
    // Priority: policyLimits (legal max) > hoursLimits > defaults
    const usedLegalDailyLimit = !!orgSettings?.policyLimits?.legalMaxHoursPerDay;
    const usedLegalWeeklyLimit = !!orgSettings?.policyLimits?.legalMaxHoursPerWeek;
    
    const dailyLimit = orgSettings?.policyLimits?.legalMaxHoursPerDay 
      ?? orgSettings?.hoursLimits?.dailyHoursLimit 
      ?? DEFAULT_THRESHOLDS.DAILY_HOURS_LIMIT;
    const weeklyLimit = orgSettings?.policyLimits?.legalMaxHoursPerWeek 
      ?? orgSettings?.hoursLimits?.weeklyHoursLimit 
      ?? DEFAULT_THRESHOLDS.WEEKLY_HOURS_LIMIT;
    
    const anomalies: Anomaly[] = [];

    // High Priority Checks
    anomalies.push(...this.checkDailyHoursExceeded(timesheet, dailyLimit, usedLegalDailyLimit));
    anomalies.push(...this.checkWeeklyHoursExceeded(timesheet, weeklyLimit, usedLegalWeeklyLimit));
    anomalies.push(...this.checkWeekendWorkNoApproval(timesheet, orgSettings));
    anomalies.push(...this.checkMissingFields(timesheet));
    anomalies.push(...this.checkTimeAndAbsenceConflict(timesheet));

    // Medium Priority Checks
    if (historicalData) {
      anomalies.push(...this.checkHoursAboveAverage(timesheet, historicalData));
      anomalies.push(...this.checkUnusualProjectAllocation(timesheet, historicalData));
    }
    anomalies.push(...this.checkLateSubmission(timesheet));

    // Low Priority Checks
    if (allTimesheets) {
      anomalies.push(...this.checkFirstProjectEntry(timesheet, allTimesheets));
    }

    // Scheduled Booking Variance Checks
    const varianceAnomalies = this.checkScheduledBookingVariances(timesheet);
    anomalies.push(...varianceAnomalies);
    
    const dismissalAnomalies = this.checkDismissedBookings(timesheet);
    anomalies.push(...dismissalAnomalies);

    // Calculate counts by severity
    const highPriorityCount = anomalies.filter(a => a.severity === 'high').length;
    const mediumPriorityCount = anomalies.filter(a => a.severity === 'medium').length;
    const lowPriorityCount = anomalies.filter(a => a.severity === 'low').length;

    return {
      timesheetId: timesheet.id,
      anomalies,
      highPriorityCount,
      mediumPriorityCount,
      lowPriorityCount,
      hasAnomalies: anomalies.length > 0,
    };
  }

  /**
   * Check for daily hours exceeding limit
   * @param timesheet - The timesheet to check
   * @param dailyLimit - The daily hours limit to use
   * @param isLegalLimit - Whether the limit is from legal maximum settings
   */
  private static checkDailyHoursExceeded(timesheet: Timesheet, dailyLimit: number, isLegalLimit: boolean = false): Anomaly[] {
    const anomalies: Anomaly[] = [];
    const dailyTotals = this.calculateDailyTotals(timesheet);

    Object.entries(dailyTotals).forEach(([day, hours]) => {
      if (hours > dailyLimit) {
        anomalies.push({
          id: `${timesheet.id}-daily-${day}`,
          type: 'daily-hours-exceeded',
          severity: 'high',
          message: isLegalLimit
            ? `${this.formatDay(day)}: ${hours.toFixed(1)}h exceeds legal daily maximum of ${dailyLimit}h`
            : `${this.formatDay(day)}: ${hours.toFixed(1)}h exceeds daily limit of ${dailyLimit}h`,
          details: isLegalLimit
            ? `Daily hours must not exceed the legal maximum of ${dailyLimit}h`
            : `Daily hours should not exceed the configured limit of ${dailyLimit}h`,
          day,
          value: hours,
          threshold: dailyLimit,
        });
      }
    });

    return anomalies;
  }

  /**
   * Check for weekly hours exceeding limit
   * @param timesheet - The timesheet to check
   * @param weeklyLimit - The weekly hours limit to use
   * @param isLegalLimit - Whether the limit is from legal maximum settings
   */
  private static checkWeeklyHoursExceeded(timesheet: Timesheet, weeklyLimit: number, isLegalLimit: boolean = false): Anomaly[] {
    const anomalies: Anomaly[] = [];
    const weeklyTotal = this.calculateWeeklyTotal(timesheet);

    if (weeklyTotal > weeklyLimit) {
      anomalies.push({
        id: `${timesheet.id}-weekly`,
        type: 'weekly-hours-exceeded',
        severity: 'high',
        message: isLegalLimit
          ? `${weeklyTotal.toFixed(1)}h exceeds legal weekly maximum of ${weeklyLimit}h`
          : `${weeklyTotal.toFixed(1)}h exceeds weekly limit of ${weeklyLimit}h`,
        details: isLegalLimit
          ? `Total weekly hours must not exceed the legal maximum of ${weeklyLimit}h`
          : `Total weekly hours should not exceed the configured limit of ${weeklyLimit}h`,
        value: weeklyTotal,
        threshold: weeklyLimit,
      });
    }

    return anomalies;
  }

  /**
   * Check for work on non-working days (uses workingProfile if available, falls back to Sat/Sun)
   */
  private static checkWeekendWorkNoApproval(timesheet: Timesheet, orgSettings?: OrganisationTimesheetSettings): Anomaly[] {
    const anomalies: Anomaly[] = [];
    const workingDays = orgSettings?.workingProfile?.workingDays;
    const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

    const nonWorkingDayHours: { day: string; hours: number }[] = [];
    let totalNonWorkingHours = 0;

    allDays.forEach(day => {
      const isNonWorking = workingDays
        ? !workingDays[day]?.isWorkingDay
        : (day === 'saturday' || day === 'sunday');

      if (isNonWorking) {
        let dayTotal = 0;
        timesheet.entries.forEach(entry => {
          dayTotal += entry.hours[day];
        });
        if (dayTotal > 0) {
          nonWorkingDayHours.push({ day: this.formatDay(day), hours: dayTotal });
          totalNonWorkingHours += dayTotal;
        }
      }
    });

    if (nonWorkingDayHours.length > 0) {
      const daysList = nonWorkingDayHours.map(d => `${d.day} (${d.hours.toFixed(1)}h)`).join(', ');
      anomalies.push({
        id: `${timesheet.id}-weekend`,
        type: 'weekend-work-no-approval',
        severity: 'high',
        message: `Non-working day work detected: ${daysList}`,
        details: 'Work on non-working days requires pre-approval from manager',
        value: totalNonWorkingHours,
      });
    }

    return anomalies;
  }

  /**
   * Check for missing required fields
   */
  private static checkMissingFields(timesheet: Timesheet): Anomaly[] {
    const anomalies: Anomaly[] = [];
    const entriesWithMissingDescription = timesheet.entries.filter(
      entry => !entry.description || entry.description.trim().length === 0
    );

    if (entriesWithMissingDescription.length > 0) {
      anomalies.push({
        id: `${timesheet.id}-missing-desc`,
        type: 'missing-description',
        severity: 'high',
        message: `${entriesWithMissingDescription.length} ${entriesWithMissingDescription.length === 1 ? 'entry' : 'entries'} missing description`,
        details: 'All timesheet entries must include a description of work performed',
        value: entriesWithMissingDescription.length,
      });
    }

    return anomalies;
  }

  /**
   * Check for time entries and absences on the same day
   */
  private static checkTimeAndAbsenceConflict(timesheet: Timesheet): Anomaly[] {
    const anomalies: Anomaly[] = [];
    
    // Track days with absences and days with regular time entries
    const daysWithAbsence = new Set<string>();
    const daysWithTimeEntry = new Set<string>();
    
    // Identify days with absence entries
    timesheet.entries.forEach(entry => {
      if (entry.absenceType || entry.sourceIntegration === 'people-hr') {
        Object.entries(entry.hours).forEach(([day, hours]) => {
          if (hours > 0) {
            daysWithAbsence.add(day);
          }
        });
      }
    });
    
    // Identify days with regular time entries (non-absence)
    timesheet.entries.forEach(entry => {
      if (!entry.absenceType && entry.sourceIntegration !== 'people-hr') {
        Object.entries(entry.hours).forEach(([day, hours]) => {
          if (hours > 0) {
            daysWithTimeEntry.add(day);
          }
        });
      }
    });
    
    // Find days with both time entries and absences
    const conflictDays = Array.from(daysWithAbsence).filter(day => daysWithTimeEntry.has(day));
    
    if (conflictDays.length > 0) {
      conflictDays.forEach(day => {
        anomalies.push({
          id: `${timesheet.id}-time-absence-${day}`,
          type: 'time-and-absence-conflict',
          severity: 'medium',
          message: `${this.formatDay(day)}: Both time entry and absence recorded on the same day`,
          details: 'Please verify this is correct. Typically, you should record either time worked or an absence, not both.',
          day,
        });
      });
    }
    
    return anomalies;
  }

  /**
   * Check for hours significantly above average
   */
  private static checkHoursAboveAverage(
    timesheet: Timesheet,
    historicalData: EmployeeHistoricalData
  ): Anomaly[] {
    const anomalies: Anomaly[] = [];
    const weeklyTotal = this.calculateWeeklyTotal(timesheet);
    const threshold = historicalData.averageWeeklyHours * DEFAULT_THRESHOLDS.HOURS_VARIANCE_THRESHOLD;

    if (weeklyTotal > threshold && historicalData.averageWeeklyHours > 0) {
      const percentageAbove = ((weeklyTotal - historicalData.averageWeeklyHours) / historicalData.averageWeeklyHours * 100).toFixed(0);
      
      anomalies.push({
        id: `${timesheet.id}-above-avg`,
        type: 'hours-above-average',
        severity: 'medium',
        message: `${weeklyTotal.toFixed(1)}h is ${percentageAbove}% above employee's average`,
        details: `Employee's average weekly hours: ${historicalData.averageWeeklyHours.toFixed(1)}h`,
        value: weeklyTotal,
        threshold: historicalData.averageWeeklyHours,
      });
    }

    return anomalies;
  }

  /**
   * Check for unusual project allocation patterns
   */
  private static checkUnusualProjectAllocation(
    timesheet: Timesheet,
    historicalData: EmployeeHistoricalData
  ): Anomaly[] {
    const anomalies: Anomaly[] = [];
    const currentProjects = new Set(timesheet.entries.map(e => e.projectId));
    const unusualProjects = Array.from(currentProjects).filter(
      projectId => !historicalData.commonProjects.includes(projectId)
    );

    if (unusualProjects.length > 0 && historicalData.commonProjects.length > 0) {
      anomalies.push({
        id: `${timesheet.id}-unusual-project`,
        type: 'unusual-project-allocation',
        severity: 'medium',
        message: `${unusualProjects.length} ${unusualProjects.length === 1 ? 'project' : 'projects'} not in employee's usual allocation`,
        details: 'Employee is working on projects outside their normal assignment',
        value: unusualProjects.length,
      });
    }

    return anomalies;
  }

  /**
   * Check for late submission
   */
  private static checkLateSubmission(timesheet: Timesheet): Anomaly[] {
    const anomalies: Anomaly[] = [];
    
    if (timesheet.submittedAt) {
      const weekEndingTime = timesheet.weekEnding.getTime();
      const submittedTime = timesheet.submittedAt.getTime();
      const daysDifference = Math.floor((submittedTime - weekEndingTime) / (1000 * 60 * 60 * 24));

      if (daysDifference > DEFAULT_THRESHOLDS.LATE_SUBMISSION_DAYS) {
        anomalies.push({
          id: `${timesheet.id}-late`,
          type: 'late-submission',
          severity: 'medium',
          message: `Submitted ${daysDifference} days after week ending`,
          details: `Timesheets should be submitted within ${DEFAULT_THRESHOLDS.LATE_SUBMISSION_DAYS} days`,
          value: daysDifference,
          threshold: DEFAULT_THRESHOLDS.LATE_SUBMISSION_DAYS,
        });
      }
    }

    return anomalies;
  }

  /**
   * Check for first-time project entries
   */
  private static checkFirstProjectEntry(
    timesheet: Timesheet,
    allTimesheets: Timesheet[]
  ): Anomaly[] {
    const anomalies: Anomaly[] = [];
    const currentProjects = new Set(timesheet.entries.map(e => e.projectId));
    
    // Get all previous timesheets for this employee
    const previousTimesheets = allTimesheets.filter(
      ts => ts.employeeId === timesheet.employeeId && 
           ts.id !== timesheet.id &&
           ts.weekEnding < timesheet.weekEnding
    );

    const previousProjects = new Set(
      previousTimesheets.flatMap(ts => ts.entries.map(e => e.projectId))
    );

    const newProjects = Array.from(currentProjects).filter(
      projectId => !previousProjects.has(projectId)
    );

    if (newProjects.length > 0) {
      anomalies.push({
        id: `${timesheet.id}-first-project`,
        type: 'first-project-entry',
        severity: 'low',
        message: `First timesheet entry on ${newProjects.length} ${newProjects.length === 1 ? 'project' : 'projects'}`,
        details: 'Employee is logging time to a new project for the first time',
        value: newProjects.length,
      });
    }

    return anomalies;
  }

  /**
   * Calculate historical data for an employee
   */
  static calculateHistoricalData(
    employeeId: number,
    allTimesheets: Timesheet[],
    orgSettings?: OrganisationTimesheetSettings
  ): EmployeeHistoricalData {
    const employeeTimesheets = allTimesheets.filter(
      ts => ts.employeeId === employeeId && ts.status === 'approved'
    );

    if (employeeTimesheets.length === 0) {
      return {
        employeeId,
        averageWeeklyHours: 0,
        averageDailyHours: 0,
        commonProjects: [],
        weekendWorkFrequency: 0,
      };
    }

    // Calculate average weekly hours
    const totalWeeklyHours = employeeTimesheets.reduce(
      (sum, ts) => sum + this.calculateWeeklyTotal(ts),
      0
    );
    const averageWeeklyHours = totalWeeklyHours / employeeTimesheets.length;

    // Calculate average daily hours (excluding non-working days)
    const workingDays = orgSettings?.workingProfile?.workingDays;
    const workingDayNames = workingDays
      ? (['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const).filter(d => workingDays[d]?.isWorkingDay)
      : ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

    let totalDailyHours = 0;
    let dayCount = 0;
    employeeTimesheets.forEach(ts => {
      const dailyTotals = this.calculateDailyTotals(ts);
      workingDayNames.forEach(day => {
        if (dailyTotals[day] > 0) {
          totalDailyHours += dailyTotals[day];
          dayCount++;
        }
      });
    });
    const averageDailyHours = dayCount > 0 ? totalDailyHours / dayCount : 0;

    // Find common projects (projects worked on in at least 30% of timesheets)
    const projectFrequency = new Map<string, number>();
    employeeTimesheets.forEach(ts => {
      const projects = new Set(ts.entries.map(e => e.projectId));
      projects.forEach(projectId => {
        projectFrequency.set(projectId, (projectFrequency.get(projectId) || 0) + 1);
      });
    });
    const threshold = employeeTimesheets.length * 0.3;
    const commonProjects = Array.from(projectFrequency.entries())
      .filter(([_, count]) => count >= threshold)
      .map(([projectId]) => projectId);

    // Calculate non-working day work frequency
    const nonWorkingDayNames = workingDays
      ? (['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const).filter(d => !workingDays[d]?.isWorkingDay)
      : (['saturday', 'sunday'] as const);
    const timesheetsWithWeekendWork = employeeTimesheets.filter(ts =>
      ts.entries.some(e => nonWorkingDayNames.some(d => e.hours[d] > 0))
    ).length;
    const weekendWorkFrequency = timesheetsWithWeekendWork / employeeTimesheets.length;

    return {
      employeeId,
      averageWeeklyHours,
      averageDailyHours,
      commonProjects,
      weekendWorkFrequency,
    };
  }

  /**
   * Calculate daily totals for a timesheet
   */
  private static calculateDailyTotals(timesheet: Timesheet): Record<string, number> {
    const totals: Record<string, number> = {
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0,
    };

    timesheet.entries.forEach(entry => {
      Object.keys(totals).forEach(day => {
        totals[day] += entry.hours[day as keyof typeof entry.hours];
      });
    });

    return totals;
  }

  /**
   * Calculate weekly total for a timesheet
   */
  private static calculateWeeklyTotal(timesheet: Timesheet): number {
    return timesheet.entries.reduce((sum, entry) => {
      return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
    }, 0);
  }

  /**
   * Format day name for display
   */
  private static formatDay(day: string): string {
    return day.charAt(0).toUpperCase() + day.slice(1);
  }

  /**
   * Get severity icon
   */
  static getSeverityIcon(severity: AnomalySeverity): string {
    switch (severity) {
      case 'high':
        return '🔴';
      case 'medium':
        return '⚠️';
      case 'low':
        return 'ℹ️';
    }
  }

  /**
   * Get severity color class
   */
  static getSeverityColorClass(severity: AnomalySeverity): string {
    switch (severity) {
      case 'high':
        return 'text-error-600 dark:text-error-400';
      case 'medium':
        return 'text-warning-600 dark:text-warning-400';
      case 'low':
        return 'text-info-600 dark:text-info-400';
    }
  }

  /**
   * Get severity background class
   */
  static getSeverityBgClass(severity: AnomalySeverity): string {
    switch (severity) {
      case 'high':
        return 'bg-error-100 dark:bg-error-950/30 border-error-300 dark:border-error-800';
      case 'medium':
        return 'bg-warning-100 dark:bg-warning-950/30 border-warning-300 dark:border-warning-800';
      case 'low':
        return 'bg-info-100 dark:bg-info-950/30 border-info-300 dark:border-info-800';
    }
  }

  /**
   * Check for scheduled booking variances
   */
  private static checkScheduledBookingVariances(timesheet: Timesheet): Anomaly[] {
    const anomalies: Anomaly[] = [];
    
    const scheduledEntries = timesheet.entries.filter(e => e.isFromSchedule && !e.isDismissed);
    if (scheduledEntries.length > 0) {
    }
    
    timesheet.entries.forEach(entry => {
      // Skip dismissed bookings (handled separately)
      if (entry.isDismissed) return;
      
      const variance = calculateEntryVariance(entry);
      
      if (!variance) {
        if (entry.isFromSchedule) {
        }
        return;
      }
      
      // Only flag significant variances (>10%)
      if (Math.abs(variance.percentage) > 10) {
        const project = entry.projectId; // Project name would need to be resolved
        const varianceText = formatVariance(variance.variance, variance.percentage);
        
        anomalies.push({
          id: `${timesheet.id}-variance-${entry.id}`,
          type: 'scheduled-booking-variance',
          severity: variance.severity,
          message: `Scheduled booking variance: ${varianceText}`,
          details: `Scheduled: ${variance.scheduled}h, Logged: ${variance.actual}h. ${
            variance.variance > 0 
              ? 'Employee logged more hours than scheduled.' 
              : 'Employee logged fewer hours than scheduled.'
          }`,
          value: variance.actual,
          threshold: variance.scheduled,
        });
      }
    });

    return anomalies;
  }

  /**
   * Check for dismissed scheduled bookings
   */
  private static checkDismissedBookings(timesheet: Timesheet): Anomaly[] {
    const anomalies: Anomaly[] = [];
    
    const dismissedBookings = timesheet.entries.filter(e => e.isDismissed);
    
    if (dismissedBookings.length > 0) {
    }
    
    dismissedBookings.forEach(entry => {
      const scheduledHours = entry.scheduledHours 
        ? Object.values(entry.scheduledHours).reduce((sum, h) => sum + h, 0)
        : 0;
      
      anomalies.push({
        id: `${timesheet.id}-dismissed-${entry.id}`,
        type: 'scheduled-booking-dismissed',
        severity: 'medium',
        message: `Scheduled booking dismissed (${scheduledHours}h scheduled)`,
        details: entry.dismissalReason 
          ? `Reason: "${entry.dismissalReason}"` 
          : 'No reason provided for dismissal',
        value: scheduledHours,
      });
    });

    return anomalies;
  }
}
